import { useEffect, useState } from 'react';
import { getCustomAdventures, deleteCustomAdventure, type CustomAdventure } from '@/utils/businessAppApi';
import { AdventureBuilderAdmin } from './AdventureBuilderAdmin';
import { CustomAdventuresList } from './CustomAdventuresList';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Sparkles, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Import design system components and tokens
import { 
  shadows, 
  borders, 
  getCardClasses,
  getShadowClasses,
  getButtonClasses
} from '@/components/business/design-system/businessDesignTokens';
import { cn } from '@/lib/utils';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24
    }
  }
};

interface AdventureManagementTabProps {
  adventures?: CustomAdventure[];
  loading?: boolean;
  onAdventuresUpdated?: () => Promise<void>;
}

export function AdventureManagementTab({ 
  adventures: propAdventures, 
  loading: propLoading = true, 
  onAdventuresUpdated 
}: AdventureManagementTabProps = {}) {
  const [adventures, setAdventures] = useState<CustomAdventure[]>(propAdventures || []);
  const [loading, setLoading] = useState(propLoading);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedAdventure, setSelectedAdventure] = useState<CustomAdventure | null>(null);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Check if we should open the builder (e.g., from parent component)
  useEffect(() => {
    const shouldOpenBuilder = sessionStorage.getItem('openAdventureBuilder');
    if (shouldOpenBuilder === 'true') {
      sessionStorage.removeItem('openAdventureBuilder');
      handleCreateAdventure();
    }

    // Listen for custom event from dashboard
    const handleOpenBuilder = () => {
      sessionStorage.removeItem('openAdventureBuilder');
      handleCreateAdventure();
    };

    window.addEventListener('openAdventureBuilder', handleOpenBuilder);
    return () => {
      window.removeEventListener('openAdventureBuilder', handleOpenBuilder);
    };
  }, []);

  // Enhanced keyboard handling for modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isBuilderOpen && e.key === 'Escape') {
        setIsBuilderOpen(false);
      }
    };

    if (isBuilderOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isBuilderOpen]);

  const fetchAdventures = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCustomAdventures();
      console.log('🔍 [AdventureManagementTab] Raw data received:', data);
      
      // Ensure we have an array
      if (!Array.isArray(data)) {
        console.error('❌ [AdventureManagementTab] Data is not an array:', data);
        throw new Error('Invalid data format received from API');
      }
      
      // Map _id to id for compatibility with the list component
      const adventuresWithId = data.map((adv: any) => ({ 
        ...adv, 
        id: adv.id || adv._id,
        // Ensure all required fields exist
        name: adv.name || 'Untitled Adventure',
        description: adv.description || 'No description available',
        options: adv.options || [],
        basePrice: adv.basePrice || 0,
        currency: adv.currency || 'USD',
        isActive: adv.isActive !== undefined ? adv.isActive : true,
        published: adv.published !== undefined ? adv.published : false
      }));
      
      console.log('✅ [AdventureManagementTab] Processed adventures:', adventuresWithId);
      console.log('📊 [AdventureManagementTab] About to set adventures state with length:', adventuresWithId.length);
      setAdventures(adventuresWithId);
      setRetryCount(0);
    } catch (err: any) {
      console.error('❌ [AdventureManagementTab] Failed to fetch custom adventures:', err);
      const errorMessage = err?.message || 'Failed to load custom adventures';
      setError(errorMessage);
      toast.error('Failed to load custom adventures', {
        description: err?.message || 'Please try again or contact support if the issue persists.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Debug: Log adventures state changes
  useEffect(() => {
    console.log('🎯 [AdventureManagementTab] Adventures state changed:', {
      length: adventures.length,
      adventures: adventures,
      loading: loading,
      hasProps: !!propAdventures
    });
  }, [adventures, loading, propAdventures]);

  // Sync with prop changes
  useEffect(() => {
    if (propAdventures) {
      console.log('🔄 [AdventureManagementTab] Syncing with prop adventures:', propAdventures);
      setAdventures(propAdventures);
      setLoading(propLoading);
    }
  }, [propAdventures, propLoading]);

  useEffect(() => {
    // Only fetch if no prop adventures provided
    if (!propAdventures) {
      fetchAdventures();
    }
  }, [propAdventures]);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteCustomAdventure(id);
      toast.success('Custom adventure deleted successfully');
      
      // Use parent callback if available, otherwise fallback to local fetch
      if (onAdventuresUpdated) {
        console.log('🔄 [AdventureManagementTab] Using parent update callback after delete');
        await onAdventuresUpdated();
      } else {
        console.log('🔄 [AdventureManagementTab] Using local fetch after delete');
        await fetchAdventures();
      }
    } catch (err: any) {
      console.error('Failed to delete custom adventure:', err);
      toast.error('Failed to delete custom adventure', { 
        description: err?.message || 'Please try again or contact support if the issue persists.'
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (adventure: CustomAdventure) => {
    setSelectedAdventure(adventure);
    setIsBuilderOpen(true);
  };

  const handleEditComplete = async () => {
    setSelectedAdventure(null);
    setIsBuilderOpen(false);
    
    // Use parent callback if available, otherwise fallback to local fetch
    if (onAdventuresUpdated) {
      console.log('🔄 [AdventureManagementTab] Using parent update callback');
      await onAdventuresUpdated();
    } else {
      console.log('🔄 [AdventureManagementTab] Using local fetch');
      await fetchAdventures();
    }
  };

  const handleCreateAdventure = () => {
    setSelectedAdventure(null);
    setIsBuilderOpen(true);
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex items-center justify-center min-h-[60vh]"
      >
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/25">
              <RefreshCw className="h-5 w-5 animate-spin text-white" />
            </div>
            <span className="text-lg font-medium text-slate-900">Loading custom adventures...</span>
          </div>
          <p className="text-sm text-slate-600">Please wait while we fetch your adventure experiences</p>
        </div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex flex-col items-center justify-center min-h-[60vh] p-6"
      >
        <Card className={getCardClasses('basic')}>
          <CardContent className="p-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 border border-amber-200/50 flex items-center justify-center mx-auto mb-4 shadow-sm">
              <svg className="w-6 h-6 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Unable to Load Adventures</h3>
            <p className="text-slate-600 mb-4 text-sm">{error}</p>
            <div className="flex flex-col gap-3">
              <Button 
                onClick={() => {
                  setRetryCount(prev => prev + 1);
                  fetchAdventures();
                }}
                className={cn(getButtonClasses('primary'), "shadow-md hover:shadow-lg transition-shadow")}
              >
                {retryCount > 0 ? `Retry (${retryCount})` : 'Retry'}
              </Button>

            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-8"
      >
        {/* Compact Create Action */}
        {!isBuilderOpen && adventures.length > 0 && (
          <motion.div variants={itemVariants} className="flex justify-end">
            <Button 
              onClick={handleCreateAdventure}
              className="bg-purple-600 hover:bg-purple-700 text-white shadow-md hover:shadow-lg transition-all duration-200"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Adventure
            </Button>
          </motion.div>
        )}

        {/* Enhanced Adventure Builder Modal */}
        <AnimatePresence>
          {isBuilderOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={(e) => {
                // Close modal when clicking backdrop
                if (e.target === e.currentTarget) {
                  setIsBuilderOpen(false);
                }
              }}
              onKeyDown={(e) => {
                // Close modal with ESC key
                if (e.key === 'Escape') {
                  setIsBuilderOpen(false);
                }
              }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 cursor-pointer"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-title"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden cursor-default"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between p-6 border-b border-slate-200/60 bg-gradient-to-r from-purple-50 to-purple-100/50">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/25">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 id="modal-title" className="text-xl font-semibold text-slate-900">
                        {selectedAdventure ? 'Edit Adventure' : 'Create New Adventure'}
                      </h2>
                      <p className="text-sm text-slate-600 mt-1">
                        {selectedAdventure ? 'Update your adventure configuration and availability' : 'Design your perfect adventure experience with pricing and availability'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsBuilderOpen(false)}
                      className="text-slate-500 hover:text-slate-700 hover:bg-white/50 rounded-lg px-3 py-2 text-sm font-medium transition-all"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsBuilderOpen(false)}
                      className="h-10 w-10 rounded-xl hover:bg-white/50 hover:scale-105 transition-all duration-200 group"
                      aria-label="Close modal"
                    >
                      <svg className="w-5 h-5 text-slate-500 group-hover:text-slate-700 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </Button>
                  </div>
                </div>
                <div className="overflow-y-auto max-h-[calc(95vh-100px)] custom-scrollbar-dark">
                  <AdventureBuilderAdmin
                    adventureToEdit={selectedAdventure}
                    onEditComplete={handleEditComplete}
                    onAdventureCreated={handleEditComplete}
                  />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Adventures List */}
        {!isBuilderOpen && (
          <motion.div variants={itemVariants}>
            <CustomAdventuresList
              adventures={adventures}
              loading={loading}
              error={error}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onCreateAdventure={handleCreateAdventure}
              deletingId={deletingId}
              onAdventureUpdated={fetchAdventures}
            />
          </motion.div>
        )}
      </motion.div>
    </>
  );
} 